import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CashflowAdmin from './CashflowAdmin';
import FprAdmin from '../components/FprAdmin';
import CheckinAdmin from '../components/CheckinAdmin';
import PdsAdmin from '../components/PdsAdmin';

/**
 * ClientForms — unified advisor surface for managing all client-facing
 * intake forms.
 *
 * Currently supports:
 *   - Personal Data Sheet (new-prospect onboarding intake)
 *   - Cashflow Wizard (annual cashflow analysis)
 *   - FPR Update Form (sent ahead of an FPR meeting)
 *   - Annual Check-In (short Red/Yellow/Green wellness questionnaire)
 *
 * The selected form type is persisted in the URL query string so links
 * can be shared and bookmarks remain stable.
 */
const FORM_TYPES = [
  { key: 'pds',      label: 'Personal Data Sheet', icon: 'bi-person-vcard' },
  { key: 'cashflow', label: 'Cashflow Wizard',   icon: 'bi-cash-stack' },
  { key: 'fpr',      label: 'FPR Update Form',   icon: 'bi-clipboard-pulse' },
  { key: 'checkin',  label: 'IM Update Form',   icon: 'bi-clipboard-pulse' },
];

const DEFAULT_TYPE = 'pds';

const getTypeFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const selectedType = params.get('type');
  return FORM_TYPES.some((tab) => tab.key === selectedType)
    ? selectedType
    : DEFAULT_TYPE;
};

const ClientForms = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeType, setActiveType] = useState(() =>
    getTypeFromSearch(location.search)
  );

  useEffect(() => {
    const nextType = getTypeFromSearch(location.search);
    setActiveType((currentType) => (
      currentType === nextType ? currentType : nextType
    ));
  }, [location.search]);

  const switchType = (key) => {
    if (key === activeType) return;
    setActiveType(key);
    const search = new URLSearchParams(location.search);
    search.set('type', key);
    navigate({ pathname: location.pathname, search: `?${search.toString()}` }, { replace: true });
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">
          <i className="bi bi-clipboard-data me-2" />
          Client Forms
        </h2>
      </div>

      <ul className="nav nav-tabs mb-4">
        {FORM_TYPES.map(({ key, label, icon }) => (
          <li key={key} className="nav-item">
            <button
              className={`nav-link ${activeType === key ? 'active' : ''}`}
              onClick={() => switchType(key)}
              type="button"
            >
              <i className={`bi ${icon} me-1`} />
              {label}
            </button>
          </li>
        ))}
      </ul>

      {activeType === 'cashflow' && <CashflowAdmin embedded />}
      {activeType === 'fpr' && <FprAdmin />}
      {activeType === 'checkin' && <CheckinAdmin />}
      {activeType === 'pds' && <PdsAdmin />}
    </div>
  );
};

export default ClientForms;
